import { IDENTITY_CONFIG, METADATA_OIDC } from '../utils/authConst';
import { UserManager, WebStorageStateStore, Log } from 'oidc-client';
import { markAsNewSessionTab, broadcastLogout } from './sessionCoordinator';

const OIDC_KEY = `oidc.user:${IDENTITY_CONFIG.authority ?? ''}:${IDENTITY_CONFIG.client_id ?? ''}`;

/**
 * Copy the OIDC user blob and tenantToken from localStorage into sessionStorage
 * so that this tab has its own working copy that survives cross-tab localStorage.clear().
 */
export function syncSessionFromLocalStorage(): void {
  const oidcData = localStorage.getItem(OIDC_KEY);
  if (oidcData) sessionStorage.setItem(OIDC_KEY, oidcData);

  const tenantToken = localStorage.getItem('tenantToken');
  if (tenantToken) sessionStorage.setItem('tenantToken', tenantToken);
}

export default class AuthService {
  UserManager;

  constructor() {
    this.UserManager = new UserManager({
      ...IDENTITY_CONFIG,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      metadata: {
        ...METADATA_OIDC,
      },
    });
    // Logger
    Log.logger = console;
    Log.level = Log.DEBUG;
    this.UserManager.events.addUserLoaded((user) => {
      if (window.location.href.includes('callback')) {
        this.navigateToScreen();
      }
    });

    this.UserManager.events.addAccessTokenExpired(() => {
      void this.signInSilent();
    });
  }

  signInRedirectCallback = async (): Promise<any> => {
    localStorage.setItem('loggedIn Time', `${String(new Date())}`);
    localStorage.setItem('Notify', 'Yes');
    const user = await this.UserManager.signinRedirectCallback();
    syncSessionFromLocalStorage();
    if (localStorage.getItem('pendingNewSession') === 'true') {
      localStorage.removeItem('pendingNewSession');
      markAsNewSessionTab();
    }
    return user;
  };

  getUser = async (): Promise<any> => {
    const user = await this.UserManager.getUser();

    if (user == null) {
      return await this.UserManager.signinRedirectCallback();
    }
    return user;
  };

  parseJwt = (token: string): any => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    return JSON.parse(window.atob(base64));
  };

  signInRedirect = async (): Promise<void> => {
    localStorage.setItem('redirectUri', window.location.pathname);
    void this.UserManager.signinRedirect({});
  };

  navigateToScreen = (): void => {
    window.location.replace('/');
  };

  isAuthenticated = (): boolean => {
    const oidcStorage = sessionStorage.getItem(OIDC_KEY) || localStorage.getItem(OIDC_KEY);

    if (oidcStorage != null) {
      const parsedStorage = JSON.parse(oidcStorage);
      return !!(Boolean(parsedStorage) && Boolean(parsedStorage.access_token));
    }

    return false;
  };

  signInSilent = async (): Promise<string | undefined | void> => {
    return this.UserManager.signinSilent({
      scope: IDENTITY_CONFIG.scope,
      response_type: IDENTITY_CONFIG.response_type,
    })
      .then(async (user) => {
        await this.UserManager.signinSilentCallback();
        // Renewed token is in localStorage — sync to this tab's sessionStorage.
        syncSessionFromLocalStorage();
        return user?.access_token;
      })
      .catch((err) => {
        console.warn('[AuthService] signInSilent failed, NOT cascading to logout:', err);
      });
  };

  signinSilentCallback = async (): Promise<void> => {
    await this.UserManager.signinSilentCallback();
  };

  createSignInRequest = (): any => {
    return this.UserManager.createSigninRequest();
  };

  logout = async (options?: { force?: boolean }): Promise<void> => {
    broadcastLogout();
    void this.UserManager.signoutRedirect({
      id_token_hint: localStorage.getItem('id_token'),
    });
    localStorage.clear();
    sessionStorage.clear();
    void this.UserManager.clearStaleState();
  };

  logoutAndRedirect = async (redirectUri?: string): Promise<void> => {
    const url =
      process.env.REACT_APP_AUTH_URL + `/Account/LoggedOut?PostLogoutRedirectUri=${redirectUri}`;
    localStorage.clear();
    sessionStorage.clear();
    window.location.replace(url);
  };

  delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  passwordLessLogin = async (redirectUri: string, intentUrl: string): Promise<void> => {
    await this.UserManager.signoutRedirectCallback();
    const tenantToken = localStorage.getItem('tenantToken');
    localStorage.clear();
    if (tenantToken != null) localStorage.setItem('tenantToken', tenantToken);
    if (intentUrl) localStorage.setItem('intentUrl', intentUrl);
    // Don't broadcast new_session_event here — the OIDC token doesn't exist yet.
    // signInRedirectCallback will broadcast after the token is stored.
    localStorage.setItem('pendingNewSession', 'true');
    window.location.href = redirectUri;
  };

  signOutRedirectCallback = async (): Promise<void> => {
    try {
      await this.UserManager.signoutRedirectCallback();
      localStorage.clear();
      window.location.replace(process.env.REACT_APP_PUBLIC_URL ?? '');
    } catch (err) {
      // Handle the error if necessary
    }

    await this.UserManager.clearStaleState();
  };

  getToken = (): string => {
    const oidcStorageString = sessionStorage.getItem(OIDC_KEY) || localStorage.getItem(OIDC_KEY);
    const oidcStorage = oidcStorageString != null ? JSON.parse(oidcStorageString) : null;
    return oidcStorage != null && Boolean(oidcStorage.access_token) ? oidcStorage.access_token : '';
  };

  getIdToken = (): string => {
    const oidcStorageString = sessionStorage.getItem(OIDC_KEY) || localStorage.getItem(OIDC_KEY);
    const oidcStorage = oidcStorageString != null ? JSON.parse(oidcStorageString) : null;
    return oidcStorage != null && Boolean(oidcStorage.id_token) ? oidcStorage.id_token : null;
  };

  getTokenForSignalR = async (): Promise<string> => {
    if (this.isTokenExpired()) {
      return (await this.signInSilent()) as string;
    }
    return this.getToken();
  };

  isTokenExpired = (): boolean => {
    const token = this.getIdToken();
    if (!token) return true;

    try {
      // Get the payload part of the JWT token
      const base64Url = token.split('.')[1];
      if (!base64Url) return true;

      // Convert base64 to JSON
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));

      // Check if the token has an expiration time
      if (!payload.exp) return true;

      // Convert expiration time to milliseconds and compare with current time
      const expirationTime = payload.exp * 1000; // exp is in seconds, convert to milliseconds
      const currentTime = Date.now();

      return currentTime >= expirationTime;
    } catch (error) {
      return true; // If we can't decode the token, assume it's expired
    }
  };
}
